import React from "react";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FilterListIcon from "@mui/icons-material/FilterList";
import { IconButton } from "@mui/material";
import styled from "@emotion/styled";

// mui components
import Typography from "../mui/Typography";
import Button from "../mui/Button";
import Stack from "../mui/Stack";
import Card from "../mui/Card";
import Box from "../mui/Box";

// common components
import MagicLoadingBox from "../common/MagicLoadingBox";

export type ScoreCardDataInter = {
  awardId: number;
  awardName: string;
  count: number;
  percentage: number;
};

interface Props {
  cardList: { cardDetails: ScoreCardDataInter[]; totalCenters: number } | {};
  isLoading: boolean;
  selectedCardStatus: string;
  handleFilter: () => void;
  handleSelectedScoreCard: (activeCardId: number, name: string, activeCardCount:number) => void;
}

const KPICards: React.FC<Props> = (props) => {
  const {
    cardList,
    selectedCardStatus,
    isLoading,
    handleFilter,
    handleSelectedScoreCard,
  } = props;

  // Conditional destructuring
  let cardDetails: ScoreCardDataInter[] = [];
  let totalCenters = 0;

  if ("cardDetails" in cardList && "totalCenters" in cardList) {
    ({ cardDetails, totalCenters } = cardList);
  }

  // handle Loading state of api data
  if (isLoading) {
    return (
      <Stack direction="row" justifyContent="space-between" mb="8px" mt="25px">
        {[1, 2, 3, 4, 5].map((cardNo) => (
          <MagicLoadingBox sx={{ width: "210px" }} height={90} key={cardNo} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="column">
      <Box sx={{ mb: "10px", display: "flex", justifyContent: "end" }}>
        <StyledButton
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={handleFilter}
        >
          Add Filters
        </StyledButton>
      </Box>

      {/*Dashboard Cards */}
      <StyledStack direction="row" flexWrap="wrap">
        <StyledCard
          onClick={() => handleSelectedScoreCard(0, "all centres",0)}
          sx={{
            borderRadius: "12px",
            border:
              selectedCardStatus == "all centres"
                ? "1px solid rgb(179, 153, 59)"
                : "1px solid rgb(217, 217, 217)",
            bgcolor:
              selectedCardStatus == "all centres"
                ? "#FFF1B7"
                : "rgb(255, 255, 255)",
            transform:
              selectedCardStatus == "all centres" ? "scale(0.98)" : "scale(1)",
            boxShadow: "none",
          }}
        >
          <Stack gap="4px">
            <CategoryTypo>All Centres</CategoryTypo>
            <CountTypo>{totalCenters}</CountTypo>
          </Stack>

          <Stack>
            <StyledIconButton
              sx={{ bgcolor: "rgb(255, 255, 255)", boxShadow: "none" }}
            >
              <NavigateNextIcon />
            </StyledIconButton>
          </Stack>
        </StyledCard>

        {cardDetails?.map((data: ScoreCardDataInter, index: number) => (
          <StyledCard
            key={index}
            onClick={() =>
              handleSelectedScoreCard(data.awardId, data.awardName, data.count)
            }
            sx={{
              borderRadius: "12px",
              border:
                selectedCardStatus === data.awardName
                  ? "1px solid rgb(179, 153, 59)"
                  : "1px solid rgb(217, 217, 217)",
              bgcolor:
                selectedCardStatus === data.awardName
                  ? "#FFF1B7"
                  : "rgb(255, 255, 255)",
              transform:
                selectedCardStatus === data.awardName
                  ? "scale(0.98)"
                  : "scale(1)",
              boxShadow: "none",
            }}
          >
            <Stack gap="4px">
              <CategoryTypo>{data.awardName}</CategoryTypo>
              <Stack direction="row">
                <CountTypo>{data.count}</CountTypo>
                <StyledPer>{`(${data.percentage}%)`}</StyledPer>
              </Stack>
            </Stack>

            <Stack>
              <StyledIconButton
                sx={{ bgcolor: "rgb(255, 255, 255)", boxShadow: "none" }}
              >
                <NavigateNextIcon />
              </StyledIconButton>
            </Stack>
          </StyledCard>
        ))}
      </StyledStack>
    </Stack>
  );
};

const StyledButton = styled(Button)((theme) => ({
  maxWidth: "140px",
  color: "black",
  textTransform: "capitalize",
  border: "1px solid #D0D5DD",
  boarderRadius: "8px",
  backgroundColor: "#FFF",
}));

const StyledStack = styled(Stack)((theme) => ({
  display: "flex",
  justifyContent: "start",
  marginBottom: "24px",
  gap: "52px",
  overflow: "scroll",
  flexWrap: "nowrap",
  overflowY: "hidden",
  scrollbarWidth: "none",
  paddingBlock: "20px",
}));

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  minWidth: "210px",
  cursor: "pointer",
  boxShadow: "none",
  transition: "transform 0.5s ease, background-color 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
    backgroundColor: "rgb(255, 248, 219)",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  "&:focus": {
    outline: "none",
  },
}));

const CategoryTypo = styled(Typography)((theme) => ({
  fontSize: "16px",
  fontWeight: 500,
}));

const CountTypo = styled(Typography)((theme) => ({
  fontSize: "24px",
  fontWeight: 600,
}));

const StyledPer = styled(Typography)((theme) => ({
  fontSize: 14,
  fontWeight: 600,
  color: "#636363",
  paddingTop: 10,
  paddingLeft: 2,
}));

const StyledIconButton = styled(IconButton)((theme) => ({
  border: "1px solid #667085",
  maxHeight: "24px",
  maxWidth: "24px",
  boxShadow: "none",
}));

export default KPICards;
